import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import useAuth from "@/providers/hooks/useAuth";
import { IPassengerHistoryList, PassengerHistoryListSchema } from "@/schemas/passenger-history.schemas";
import { IPlanPassenger, IResPassengersSearchResult } from "@/types/tasks.types";

export const useGetPassengerHistory = ({ passengerId }: { passengerId: string | null }) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();
    return useQuery<IPassengerHistoryList>({
        enabled: !!auth?.accessToken && !!passengerId,
        queryKey: [QUERY_KEYS.GET_PASSENGER_HISTORY, passengerId],
        gcTime: 0, // No garbage collection time (no caching)
        queryFn: async () => {
            try {
                const res = await axiosInstance
                    .get<IPassengerHistoryList>(`/passengers/${passengerId}/history`)
                    .then(({ data }) => data);
                const validResponse = PassengerHistoryListSchema.safeParse(res);
                if (!validResponse.success) {
                    Sentry.captureException(new Error("Invalid response"), {
                        extra: {
                            response: res,
                        },
                    });
                    return res as IPassengerHistoryList;
                }
                return validResponse.data;
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
    });
};

const PASSENGER_SEARCH_LIMIT = 10;
export const useGetPassengerSearch = ({ name, flightNumber }: { name?: string; flightNumber?: string }) => {
    const axiosInstance = useGetTransportAxiosInstance();
    const nameKey = name ? name.toLowerCase() : "undefined";
    const flightNumberKey = flightNumber ? flightNumber.toLowerCase() : "undefined";
    const queryKey = `operation:search-by-filters:${nameKey}:${flightNumberKey}`;

    const query = new URLSearchParams({ limit: PASSENGER_SEARCH_LIMIT.toString() });
    if (name) query.set("name", name);
    if (flightNumber) query.set("flightNumber", flightNumber);

    return useQuery<IResPassengersSearchResult[]>({
        queryKey: [QUERY_KEYS.GET_PASSENGER_SEARCH, queryKey],
        retry: false,
        enabled: !!name || !!flightNumber,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            try {
                const res = await axiosInstance
                    .get<IResPassengersSearchResult[]>(`/passengers/search?${query.toString()}`)
                    .then(({ data }) => data);
                return res;
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
    });
};

export const useGetFormattedPassenger = ({ passengerId }: { passengerId: string | null }) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();
    return useQuery<IPlanPassenger<"db">>({
        enabled: !!auth?.accessToken && !!passengerId,
        queryKey: [QUERY_KEYS.GET_FORMATTED_PASSENGER, passengerId],
        gcTime: 0, // No garbage collection time (no caching)
        queryFn: async () => {
            try {
                const res = await axiosInstance
                    .get<IPlanPassenger<"db">>(`/passengers/${passengerId}/formatted`)
                    .then(({ data }) => data);
                return res;
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
    });
};
